<script lang="ts" setup>
import { useRouter } from "vue-router";
import type { PartnerClient } from "@/types/project-types";
import DataView from "@/components/DataView.vue";
import type { Config } from "datatables.net";

const props = defineProps<{
  clients: PartnerClient[];
  loading?: boolean;
}>();

const router = useRouter();

function openClient(client: PartnerClient) {
  router.push(`/partners/clients/${client.id}`);
}

const getHealthStatusColor = (status: string) => {
  switch (status) {
    case "healthy": return "text-success";
    case "warning": return "text-warning";
    case "error": return "text-danger";
    default: return "text-secondary";
  }
};

const getHealthStatusIcon = (status: string) => {
  switch (status) {
    case "healthy": return "bi-check-circle-fill";
    case "warning": return "bi-exclamation-triangle-fill";
    case "error": return "bi-x-circle-fill";
    default: return "bi-question-circle-fill";
  }
};

const getStatusBadge = (status: string) => {
  switch (status) {
    case "active": return "bg-success";
    case "inactive": return "bg-secondary";
    default: return "bg-secondary";
  }
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString();
};

const formatTimeAgo = (timestamp: string) => {
  const now = new Date();
  const activityTime = new Date(timestamp);
  const diffMs = now.getTime() - activityTime.getTime();
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return "1 day ago";
  return `${diffDays} days ago`;
};

const rateClass = (rate: number) =>
  rate >= 90 ? "text-success" : rate >= 80 ? "text-warning" : "text-danger";

const rateBarClass = (rate: number) =>
  rate >= 90 ? "bg-success" : rate >= 80 ? "bg-warning" : "bg-danger";

const tableOptions: Config = {
  columns: [
    {
      data: "organizationName",
      title: "Organization",
      render: (d: string, _type: string, row: any) =>
        `<div><strong>${d}</strong><br><small class="text-muted">Joined ${formatDate(row.joinedDate)}</small></div>`,
    },
    {
      data: "contactName",
      title: "Contact",
      render: (d: string, _type: string, row: any) =>
        `<div>${d}<br><small class="text-muted">${row.contactEmail}</small></div>`,
    },
    {
      data: "status",
      title: "Status",
      render: (d: string) =>
        `<span class="badge text-uppercase ${getStatusBadge(d)}">${d}</span>`,
    },
    {
      data: "projectCount",
      title: "Projects",
      render: (d: number) => `<span class="fw-bold">${d}</span>`,
    },
    {
      data: "successRate",
      title: "Success Rate",
      render: (d: number) =>
        `<div class="d-flex align-items-center"><span class="fw-bold me-2 ${rateClass(d)}">${d.toFixed(1)}%</span><div class="progress" style="width:60px;height:4px"><div class="progress-bar ${rateBarClass(d)}" style="width:${d}%"></div></div></div>`,
    },
    {
      data: "totalRuns",
      title: "Total Runs",
      render: (d: number) => `<span class="fw-bold">${d.toLocaleString()}</span>`,
    },
    {
      data: "healthStatus",
      title: "Health",
      render: (d: string) =>
        `<i class="bi ${getHealthStatusIcon(d)} ${getHealthStatusColor(d)}" title="${d}"></i>`,
    },
    {
      data: "lastActivity",
      title: "Last Activity",
      render: (d: string) => `<small class="text-muted">${formatTimeAgo(d)}</small>`,
    },
    {
      data: null,
      title: "Actions",
      orderable: false,
      render: () =>
        `<div class="d-flex gap-1"><button class="bb-btn-secondary-cta btn-sm text-nowrap"><i class="bi bi-eye me-1"></i>View</button><button class="bb-btn-secondary-cta btn-sm text-nowrap"><i class="bi bi-gear me-1"></i>Manage</button></div>`,
    },
  ] as any,
  layout: {
    topStart: null,
    topEnd: null,
    bottomEnd: null,
    bottomStart: null,
  },
};
</script>

<template>
  <div class="partner-clients">
    <div v-if="loading" class="text-center py-4">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <DataView
      v-else
      :data="clients"
      :view-mode="'table'"
      :options="tableOptions"
      @row-click="(client: PartnerClient) => openClient(client)"
    >
      <!-- Mobile card — kept visually identical to the full clients page
           (PartnersClientsView.vue). If you change one, change both. -->
      <template #grid-card="{ row: client }">
        <div class="card h-100 card-hover">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-start gap-2 mb-2">
              <div class="min-w-0">
                <h4 class="mb-0 fw-medium text-truncate">{{ client.organizationName }}</h4>
                <small class="text-muted">{{ client.contactName }}</small>
              </div>
              <div class="d-flex align-items-center gap-2 flex-shrink-0">
                <span class="badge rounded-pill text-uppercase" :class="getStatusBadge(client.status)">
                  {{ client.status }}
                </span>
                <i
                  :class="[getHealthStatusIcon(client.healthStatus), getHealthStatusColor(client.healthStatus)]"
                  :title="client.healthStatus"
                ></i>
              </div>
            </div>

            <!-- Inline metric row — flat, no nested cards (matches the canonical grid-card pattern) -->
            <div class="d-flex flex-wrap column-gap-4 row-gap-1 small">
              <div>
                <span class="fw-bold">{{ client.projectCount }}</span>
                <span class="text-muted ms-1">Projects</span>
              </div>
              <div>
                <span class="fw-bold" :class="rateClass(client.successRate)">
                  {{ client.successRate.toFixed(1) }}%
                </span>
                <span class="text-muted ms-1">Success</span>
              </div>
              <div>
                <span class="fw-bold">{{ client.totalRuns.toLocaleString() }}</span>
                <span class="text-muted ms-1">Runs</span>
              </div>
            </div>
          </div>

          <div class="card-footer bg-transparent border-top-0 d-flex justify-content-between align-items-center">
            <small class="text-muted"><i class="bi bi-clock me-1"></i>{{ formatTimeAgo(client.lastActivity) }}</small>
            <button class="bb-btn-secondary-cta btn-sm text-nowrap" @click="openClient(client)">
              <i class="bi bi-eye me-1"></i>View
            </button>
          </div>
        </div>
      </template>

      <template #empty-state>
        <div class="text-center py-4 text-muted">
          <i class="bi bi-people fs-1 mb-2"></i>
          <p>No partner clients found</p>
        </div>
      </template>
    </DataView>

    <div v-if="!loading && clients.length > 0" class="text-center pt-3 border-top">
      <router-link to="/partners/clients" class="bb-btn-secondary-cta btn-sm">
        <i class="bi bi-arrow-right me-1"></i>View All Clients
      </router-link>
    </div>
  </div>
</template>

<style scoped lang="scss">
.partner-clients {
  min-height: 300px;
}

.progress {
  border-radius: 2px;
}
</style>
